<template>
  <transition :name="animate ? 'slide' : null">
    <div v-if="visible && text" class="age-info-box desktop-narrow" :class="bgClass">
      <ScImage v-if="icon" :media="icon" class="age-info-box__icon" />
      <ScText v-if="text" tag="p" :field="text" class="text-s" />
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { Text, Image } from '@sitecore-jss/sitecore-jss-vue';
import { calculateAge } from '../../../Talkmore.Web.Vue.Shared/src/utils';

export default {
  name: 'AgeInfoBox',
  components: {
    ScText: Text,
    ScImage: Image,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      visible: false,
      animate: false,
      ownerBirthday: this.$store.state.app?.birthday ?? null,
      age: null,
      currentAgeInfoBoxItem: null,
    };
  },
  computed: {
    ...mapGetters({
      // Todo: fix getters not being reactive when changing values inside array
      // after this is fixed we can again use this getter
      // currentItem: 'basket/getCurrentItem',
      isSingleFlow: 'config/isSingleFlow',
      priceKeyNormal: 'config/getPriceKeyNormal',
      priceKeyU30: 'config/getPriceKeyU30',
    }),
    bgClass() {
      return this.currentAgeInfoBoxItem?.fields?.AgeInfoBoxItemBackgroundColor?.fields?.Type?.value;
    },
    text() {
      return this.currentAgeInfoBoxItem?.fields?.AgeInfoBoxItemText;
    },
    icon() {
      return this.currentAgeInfoBoxItem?.fields?.AgeInfoBoxItemIcon;
    },
    priceKeyTarget() {
      return this.currentAgeInfoBoxItem?.fields?.AgeInfoBoxItemPriceKeyTarget?.value;
    },
    userBirthday() {
      return this.$store.state.basket?.currentItem?.user?.birthday;
    },
    event() {
      // use value for radio buttons (as it is unique), and key for input fields (as they don't have a value field)
      return this.fields.ToggleByComponentValue?.fields.Value?.value ?? this.fields.ToggleByComponentValue?.fields.Key?.value;
    },
    currentItemPriceKey() {
      return this.$store.state.basket?.currentItem?.priceKey;
    },
  },
  watch: {
    userBirthday() {
      this.toggle();
    },
  },
  mounted() {
    this.$root.$on('changePrice', () => {
      if (this.visible && this.age) {
        this.changePrice();
      }
    });
    if (this.fields.ToggleByComponentValue) {
      this.$root.$on(this.event, (data) => {
        this.animate = true;
        // toggled on radio button
        if (data === 'true' && this.ownerBirthday) this.toggle(this.ownerBirthday);
        // toggled off radio button
        else if (data === 'false') this.visible = false;
        else this.toggle(data); // data should be a date string (DD.MM.YYYY) in this case
      });
    }
  },
  beforeDestroy() {
    if (this.fields.ToggleByComponentValue) this.$root.$off(this.event);
    this.$root.$off('changePrice');
  },
  methods: {
    toggle(userBirthday = this.userBirthday) {
      if (!this.currentItemPriceKey || !userBirthday) return;

      const age = calculateAge(userBirthday);
      if (!Number.isFinite(age) || age < 0) return;

      this.age = age;

      const eligibleItems = this.fields.AgeInfoBoxItems.filter((item) => {
        const maxAge = item.fields.AgeInfoBoxItemMaxAge?.value;
        return maxAge > this.age;
      });

      // Sort eligible items by maxAge in ascending order
      const sortedEligibleItems = eligibleItems.sort((a, b) => {
        const maxAgeA = a.fields.AgeInfoBoxItemMaxAge?.value || Infinity;
        const maxAgeB = b.fields.AgeInfoBoxItemMaxAge?.value || Infinity;
        return maxAgeA - maxAgeB;
      });

      let currentAgeInfoBoxItem = null;
      let isEligibleProduct = false;

      for (const item of sortedEligibleItems) {
        const eligibleProducts = item?.fields?.AgeInfoBoxItemEligibleProducts;

        if (!eligibleProducts || eligibleProducts.length === 0) {
          // If there are no eligible products, consider it eligible
          isEligibleProduct = true;
        } else {
          // Check if the current item is in the list of eligible products
          isEligibleProduct = eligibleProducts.some((product) => {
            return (
              product.fields['CRM Item']?.value === this.$store.state.basket?.currentItem?.product?.fields['CRM Item']?.value
            );
          });
        }

        if (isEligibleProduct) {
          currentAgeInfoBoxItem = item;
          break;
        }
      }

      // Fallback to the item with no max age set ("Normal") if no eligible items are found
      if (!currentAgeInfoBoxItem) {
        currentAgeInfoBoxItem = this.fields.AgeInfoBoxItems.find((item) => !item.fields.AgeInfoBoxItemMaxAge?.value);
        isEligibleProduct = true; // Set to true if no eligible products are found
      }

      this.currentAgeInfoBoxItem = currentAgeInfoBoxItem;

      this.visible = this.currentAgeInfoBoxItem && this.priceKeyTarget !== this.currentItemPriceKey && isEligibleProduct;
    },
    changePrice() {
      if (!this.priceKeyTarget) return;
      const value = this.priceKeyTarget;

      this.$store.dispatch('basket/appendToCurrentItem', { key: 'priceKey', value });
      if (this.isSingleFlow) {
        this.$store.dispatch('config/updatePriceKey', value);
        this.$store.dispatch('config/updateRatePlanPrices');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.slide-enter-active {
  @include transition(0.3s);
  overflow: hidden;
}
.slide-enter-to {
  max-height: 200px;
  opacity: 1;
}
.slide-enter {
  max-height: 0;
  opacity: 0;
}

.age-info-box {
  border-radius: 16px;
  display: flex;
  align-items: center;
  padding: $spacing-s;
  margin-bottom: $spacing-s;
  gap: 12px;

  img {
    width: 39px;
    height: 36px;
  }
}
</style>
